/* ============
   FTB - floating launcher collision manager
   Built 2026-08-29. Draft theme 196631724198.

   THE PROBLEM THIS EXISTS TO SOLVE, IN ONE LINE:
   Shopify Inbox mounts <shopify-chat> as a direct child of <body> at
   z-index 2147483000, fixed to a bottom corner. Nothing on the page can
   out-stack it, so whatever scrolls under it stops taking taps.

   Three surfaces have now hit this, all measured, all the same failure:
     2026-08-24  the cart drawer's exits        13% and 15% covered
     2026-08-26  the quick-view drawer          69 x 39px over its ADD TO CART
     2026-08-29  the product page ADD TO CART   83.73 x 44.84px, 39.3% of it
   The first two are overlays, so a CSS :has() hook was enough and they are
   handled in ftb-a11y.css section 8. The third is not an overlay. Nothing in
   the DOM changes when ADD TO CART scrolls into the launcher, so CSS has no
   hook and this file supplies one.

   WHAT IT DOES: sets .ftb-chat-clear on <body> while a protected control is
   on screen AND the launcher's horizontal span overlaps that control's.
   ftb-a11y.css section 10 does the hiding. This file never styles anything.

   WHY A HORIZONTAL-SPAN TEST AND NOT A BREAKPOINT: the horizontal spans do
   not move when the page scrolls, so the test is a stable width check with no
   magic number in it. At 390 the pill (x271.50-355.23) sits inside the button
   (x145.99-359.23) and it reads true. At 1579 the button is x1012.1-1432.1
   against a pill at x1460.5-1544.2 and it reads false, so chat stays put on
   desktop. It also self-corrects if the Inbox admin's left/right setting
   changes, or if Broadcast's column maths ever moves the button.

   [!] IT MUST NOT HIDE A CHAT THE SHOPPER HAS OPENED. Two guards below:
   a null launcher box never hides anything, and one click anywhere on the
   host retires this script for the rest of the page view.

   [!] ftb-a11y.css SECTION 10 USES visibility, NOT display. This script
   measures the launcher every frame, and display:none would collapse its box
   to 0x0 and start a flicker loop. Read that comment before changing either.

   QA HANDLE: window.FTBLaunchers.update() forces a recalculation, and
   window.FTBLaunchers.box() returns the launcher rect the script is using.
   Both exist because requestAnimationFrame does not fire while a document is
   hidden, and the measuring harness on this project always reads
   document.visibilityState as "hidden".
   ============ */
(function () {
  'use strict';

  /* Controls that must never sit under a floating launcher.
     ADD TO THIS LIST as the page review reaches each surface. */
  var PROTECTED = [
    '.product__submit__buttons button[name="add"]'
  ];

  var BODY_CLASS = 'ftb-chat-clear';
  var frame = 0;
  var timer = 0;
  var hunts = 0;
  var retired = false;

  /* The collapsed pill's rect, or null if it cannot be trusted.
     Layer 1 is the class Shopify ships today. Layer 2 is a name-independent
     geometric scan, so a rename upstream degrades to "still works" rather
     than "silently stops". If both fail we return null and hide nothing,
     which restores the old defect rather than inventing a wrong box. */
  function launcherBox() {
    var host = document.querySelector('shopify-chat');
    if (!host) return null;
    var hostRect = host.getBoundingClientRect();
    if (!hostRect.width || !hostRect.height) return null;
    var root = host.shadowRoot;
    if (!root) return null;

    var named = root.querySelectorAll('[class*="activator"]');
    var i, r;
    for (i = 0; i < named.length; i++) {
      r = named[i].getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && r.width < hostRect.width) return r;
    }

    var all = root.querySelectorAll('*');
    var best = null;
    for (i = 0; i < all.length; i++) {
      r = all[i].getBoundingClientRect();
      if (r.width < 24 || r.height < 24) continue;
      if (r.width >= hostRect.width * 0.6) continue;
      if (r.height >= hostRect.height * 0.4) continue;
      if (!best || r.width * r.height > best.width * best.height) best = r;
    }
    return best;
  }

  /* True when some protected control is on screen vertically AND the launcher
     shares its horizontal span. Re-queries the DOM each pass so a form that
     Broadcast re-renders on a variant change is picked up for free. */
  function collides(box) {
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    var i, j, els, r;
    for (i = 0; i < PROTECTED.length; i++) {
      els = document.querySelectorAll(PROTECTED[i]);
      for (j = 0; j < els.length; j++) {
        r = els[j].getBoundingClientRect();
        if (!r.width || !r.height) continue;
        if (r.bottom <= 0 || r.top >= vh) continue;
        if (box.left < r.right && box.right > r.left) return true;
      }
    }
    return false;
  }

  function update() {
    var body = document.body;
    if (!body) return;
    var box = retired ? null : launcherBox();
    if (box && collides(box)) body.classList.add(BODY_CLASS);
    else body.classList.remove(BODY_CLASS);
  }

  function run() {
    if (frame) { window.cancelAnimationFrame(frame); frame = 0; }
    if (timer) { window.clearTimeout(timer); timer = 0; }
    update();
  }

  function schedule() {
    if (frame || timer) return;
    frame = window.requestAnimationFrame(run);
    timer = window.setTimeout(run, 200);
  }

  /* The shopper opened or closed the chat. Stop suppressing it for the rest
     of this page view and put it back. Fails safe in both directions. */
  function retire() {
    retired = true;
    var body = document.body;
    if (body) body.classList.remove(BODY_CLASS);
  }

  /* <shopify-chat> mounts asynchronously and its activator lands later
     still, so poll for a usable box rather than reading once at load. */
  function hunt() {
    schedule();
    var host = document.querySelector('shopify-chat');
    if (host) {
      host.addEventListener('click', retire, true);
      if (launcherBox()) return;
    }
    hunts += 1;
    if (hunts > 24) return;
    window.setTimeout(hunt, 500);
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });

  window.FTBLaunchers = {
    update: update,
    box: launcherBox,
    protectedSelectors: PROTECTED
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hunt);
  } else {
    hunt();
  }
})();

/* FTB 2026-11-03: LOADS assets/ftb-tour-split.js ON EVERY PAGE (tours and merchandise
   check out separately). This file was chosen only because it already loads site-wide;
   the two features share nothing. Its URL is this script's own, with the name swapped. */
(function () {
  var me = document.querySelector('script[src*="ftb-launchers.js"]');
  if (!me || document.querySelector('script[src*="ftb-tour-split.js"]')) return;
  var s = document.createElement('script');
  s.src = me.src.split('?')[0].replace('ftb-launchers.js', 'ftb-tour-split.js') + '?v=20261103a'; /* bump this when ftb-tour-split.js changes */
  s.defer = true;
  document.head.appendChild(s);
})();
